import { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlass, X } from 'shared/icons';
import { useDebounce } from '../../hooks/useDebounce';
import api from '../../services/api';
import Input from '../ui/Input';
import { MONO_CLASS, Mono, Sku } from '../ui/Mono';
import Select from '../ui/Select';
import { ExtractListRows } from './payload';

/**
 * Seletores compartilhados do M3 (LPN & Recebimento): a PÁGINA (filtros) e o
 * MODAL de criação escolhem as mesmas coisas — posição, depósito, prefixo e
 * produto do catálogo. Ficam num módulo só para não nascerem dois seletores de
 * posição com regras diferentes (CLAUDE.md §4).
 *
 * Todos os hooks recebem `isActive`: cadastro só é buscado quando o consumidor
 * está de fato na tela (modal fechado não faz GET).
 */

// Universo de posições ativas cabe numa tacada (M1 fechou em ~456 endereços;
// o teto do wire é 5000). O seletor filtra em memória — é instantâneo e não
// depende de busca no servidor, que a rota de localizações não tem.
const LOCATIONS_LIMIT = 5000;
// Opções mostradas por vez no select de posição: a lista inteira num <select>
// é inutilizável no teclado. Refine pelo campo de filtro para chegar na sua.
const MAX_LOCATION_OPTIONS = 200;
// Resultados da busca de produto (catálogo grande, paginação no servidor).
const PRODUCT_SEARCH_LIMIT = 12;
// Busca de produto só sai com 2+ caracteres: 1 letra devolve o catálogo inteiro.
const PRODUCT_SEARCH_MIN_CHARS = 2;

// Campo de FILTRO dentro de um <form> (o modal de recebimento): Enter aqui é
// "achei", não "salva o LPN" — sem isto o formulário submete pela metade.
function blockEnterSubmit(event) {
    if (event.key === 'Enter') {
        event.preventDefault();
    }
}

const LOAD_LPN_PREFIXES = () => api.getWarehouseLpnPrefixes({ active: 'true', limit: 500 });
const LOAD_DEPOSITS = () => api.getWarehouseDeposits({ active: 'true', limit: 500 });
const LOAD_LOCATIONS = () => api.getWarehouseLocations({ status: 'ACTIVE', limit: LOCATIONS_LIMIT });

const LPN_PREFIX_KEYS = ['lpn_prefixes', 'prefixes'];
const DEPOSIT_KEYS = ['deposits'];
const LOCATION_KEYS = ['locations'];

function useRegistryOptions(loader, listKeys, isActive) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
        if (!isActive) {
            return undefined;
        }
        let alive = true;
        setLoading(true);
        setLoadError(null);
        loader()
            .then((data) => {
                if (alive) {
                    setRows(ExtractListRows(data, listKeys));
                }
            })
            .catch((error) => {
                if (alive) {
                    setLoadError(error?.message || 'Erro ao carregar');
                    setRows([]);
                }
            })
            .finally(() => {
                if (alive) {
                    setLoading(false);
                }
            });
        return () => {
            alive = false;
        };
    }, [loader, listKeys, isActive]);

    return { rows, loading, loadError };
}

/** Prefixos de LPN ATIVOS (cadastro do M0 — identidade, nunca comportamento). */
export function useActiveLpnPrefixes(isActive) {
    return useRegistryOptions(LOAD_LPN_PREFIXES, LPN_PREFIX_KEYS, isActive);
}

/** Depósitos ATIVOS (o depósito é contábil — nunca bloqueia posição física). */
export function useActiveDeposits(isActive) {
    return useRegistryOptions(LOAD_DEPOSITS, DEPOSIT_KEYS, isActive);
}

/** Posições ATIVAS, ordenadas pelo código (é o código que o operador conhece). */
export function useActiveLocations(isActive) {
    const { rows, loading, loadError } = useRegistryOptions(LOAD_LOCATIONS, LOCATION_KEYS, isActive);
    const sorted = useMemo(
        () => [...rows].sort((a, b) => String(a?.code || '').localeCompare(String(b?.code || ''))),
        [rows],
    );
    return { rows: sorted, loading, loadError };
}

/**
 * Seletor de POSIÇÃO: campo de filtro (por código) + select das ativas. Não é
 * combobox de biblioteca — é input + <select> nativo, que funciona no teclado
 * e no coletor sem dependência nova. A posição escolhida continua na lista
 * mesmo quando o filtro a esconderia (senão o campo "esvaziava" sozinho).
 */
export const LocationPicker = ({
    label,
    locations = [],
    loading = false,
    value = '',
    onChange,
    disabled = false,
    errorMessage,
    emptyOptionText = null,
}) => {
    const [filter, setFilter] = useState('');

    const selectedRow = useMemo(
        () => locations.find((row) => String(row?.id) === String(value)) || null,
        [locations, value],
    );

    const options = useMemo(() => {
        const term = filter.trim().toUpperCase();
        const matches = term
            ? locations.filter((row) =>
                  String(row?.code || '')
                      .toUpperCase()
                      .includes(term),
              )
            : locations;
        const visible = matches.slice(0, MAX_LOCATION_OPTIONS);
        if (selectedRow && !visible.some((row) => String(row.id) === String(selectedRow.id))) {
            return [selectedRow, ...visible];
        }
        return visible;
    }, [locations, filter, selectedRow]);

    const hiddenCount = Math.max(0, locations.length - options.length);

    return (
        <div className="space-y-2">
            <Input
                label={label('wms.lpns.location_filter')}
                value={filter}
                onChange={(event) => setFilter(event.target.value.toUpperCase())}
                onKeyDown={blockEnterSubmit}
                placeholder={label('wms.lpns.location_filter_placeholder')}
                disabled={disabled || loading}
                autoComplete="off"
                className={MONO_CLASS}
            />
            <Select
                label={label('wms.lpn.field.location')}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                disabled={disabled || loading}
                errorMessage={errorMessage}
                className={MONO_CLASS}
            >
                {emptyOptionText !== null ? <option value="">{emptyOptionText}</option> : null}
                {!loading && locations.length === 0 ? (
                    <option value="" disabled>
                        {label('wms.lpns.no_locations')}
                    </option>
                ) : null}
                {options.map((row) => (
                    <option key={row.id} value={String(row.id)}>
                        {row.code}
                    </option>
                ))}
            </Select>
            {hiddenCount > 0 ? (
                <p className="text-xs text-mono-500 dark:text-mono-400">
                    {`${label('wms.lpns.location_more')} ${hiddenCount}`}
                </p>
            ) : null}
        </div>
    );
};

/**
 * Busca de PRODUTO no catálogo (GET /warehouse/products?search= — paginação no
 * servidor, o catálogo nunca desce inteiro). Digitou 2+ caracteres, a lista de
 * resultados aparece; clicou, vira o produto escolhido (chip com SKU + nome e
 * um X para trocar). `value` é a linha do produto ou null.
 */
export const ProductPicker = ({ label, fieldLabel, value = null, onChange, disabled = false, autoFocus = false }) => {
    const [term, setTerm] = useState('');
    const [rows, setRows] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    // Guarda contra resposta fora de ordem (digitação rápida).
    const requestIdRef = useRef(0);
    const debouncedTerm = useDebounce(term, 300);

    useEffect(() => {
        const search = debouncedTerm.trim();
        if (value || search.length < PRODUCT_SEARCH_MIN_CHARS) {
            setRows([]);
            setSearchError(null);
            setSearching(false);
            return undefined;
        }
        const requestId = ++requestIdRef.current;
        setSearching(true);
        setSearchError(null);
        api.getWarehouseProducts({ search, limit: PRODUCT_SEARCH_LIMIT, offset: 0 })
            .then((data) => {
                if (requestId === requestIdRef.current) {
                    setRows(ExtractListRows(data, ['results', 'products']));
                }
            })
            .catch((error) => {
                if (requestId === requestIdRef.current) {
                    setSearchError(error?.message || label('wms.common.load_error'));
                    setRows([]);
                }
            })
            .finally(() => {
                if (requestId === requestIdRef.current) {
                    setSearching(false);
                }
            });
        return undefined;
    }, [debouncedTerm, value, label]);

    const pick = (row) => {
        setTerm('');
        setRows([]);
        onChange?.(row);
    };

    if (value) {
        return (
            <div>
                <span className="mb-1 block text-sm font-medium text-mono-700 dark:text-mono-300">{fieldLabel}</span>
                <div className="flex items-center gap-2 rounded-lg border border-mono-300 px-3 py-2 dark:border-mono-700">
                    <Sku value={value.sku} className="text-sm font-medium" />
                    <span className="min-w-0 flex-1 truncate text-sm text-mono-600 dark:text-mono-400">
                        {value.name}
                    </span>
                    <button
                        type="button"
                        onClick={() => onChange?.(null)}
                        disabled={disabled}
                        aria-label={label('wms.lpns.clear_product')}
                        title={label('wms.lpns.clear_product')}
                        className="shrink-0 rounded p-1 transition-colors hover:bg-mono-100 disabled:opacity-50 dark:hover:bg-mono-900"
                    >
                        <X size={14} className="text-mono-600 dark:text-mono-400" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <Input
                label={fieldLabel}
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                onKeyDown={blockEnterSubmit}
                placeholder={label('wms.products.search_placeholder')}
                disabled={disabled}
                autoComplete="off"
                autoFocus={autoFocus}
            />
            {searchError ? <p className="text-sm text-danger-600 dark:text-danger-400">{searchError}</p> : null}
            {searching ? (
                <p className="flex items-center gap-1.5 text-xs text-mono-500 dark:text-mono-400">
                    <MagnifyingGlass size={12} />
                    {label('wms.lpns.searching')}
                </p>
            ) : null}
            {!searching && rows.length > 0 ? (
                <ul className="max-h-52 divide-y divide-mono-100 overflow-y-auto rounded-lg border border-mono-200 dark:divide-mono-900 dark:border-mono-800">
                    {rows.map((row) => (
                        <li key={row.product_id}>
                            <button
                                type="button"
                                onClick={() => pick(row)}
                                disabled={disabled}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-mono-50 dark:hover:bg-mono-900"
                            >
                                <Mono className="text-xs font-medium text-black dark:text-white">{row.sku}</Mono>
                                {/* EAN ao lado do SKU: é o que a logística confere. */}
                                {row.ean ? (
                                    <Mono className="text-xs text-mono-500 dark:text-mono-400">{row.ean}</Mono>
                                ) : null}
                                <span className="min-w-0 flex-1 truncate text-sm text-mono-600 dark:text-mono-400">
                                    {row.name}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
            {!searching &&
            !searchError &&
            debouncedTerm.trim().length >= PRODUCT_SEARCH_MIN_CHARS &&
            rows.length === 0 ? (
                <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.products.empty')}</p>
            ) : null}
        </div>
    );
};
